import LogUtil from '../../../core/utils/LogUtil';
import { getPharmacyAuthService } from '../../pharmacyAuth';

/**
 * Prescription Service
 * 처방전 관리 비즈니스 로직 계층
 */
export class PrescriptionService {
  constructor(prescriptionRepository) {
    this.prescriptionRepository = prescriptionRepository;
    LogUtil.log('🔧 [PrescriptionService] 초기화 완료');
  }

  /**
   * 처방전 업로드
   * @param {Object} data - 처방전 데이터
   * @returns {Promise<BaseResponse<any>>}
   */
  async uploadPrescription(data) {
    try {
      LogUtil.log('📤 [PrescriptionService] 처방전 업로드 요청:', data);

      // 데이터 검증
      if (!data.image_url) {
        throw new Error('처방전 이미지 URL이 필요합니다.');
      }

      const result = await this.prescriptionRepository.uploadPrescription(data);
      LogUtil.log('📥 [PrescriptionService] 처방전 업로드 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] 처방전 업로드 실패:', error);
      throw error;
    }
  }

  /**
   * 처방전 목록 조회
   * @param {Object} params - 쿼리 파라미터
   * @returns {Promise<BaseResponse<any>>}
   */
  async getPrescriptions(params = {}) {
    try {
      LogUtil.log('📤 [PrescriptionService] 처방전 목록 조회 요청:', params);
      const result = await this.prescriptionRepository.getPrescriptions(params);
      LogUtil.log('📥 [PrescriptionService] 처방전 목록 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] 처방전 목록 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 처방전 상세 조회
   * @param {number} prescriptionId - 처방전 ID
   * @returns {Promise<BaseResponse<any>>}
   */
  async getPrescriptionById(prescriptionId) {
    try {
      LogUtil.log(
        '📤 [PrescriptionService] 처방전 상세 조회 요청:',
        prescriptionId
      );

      if (!prescriptionId) {
        throw new Error('처방전 ID가 필요합니다.');
      }

      const result =
        await this.prescriptionRepository.getPrescriptionById(prescriptionId);
      LogUtil.log('📥 [PrescriptionService] 처방전 상세 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] 처방전 상세 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 약국 선택 및 주문 생성
   * @param {number} prescriptionId - 처방전 ID
   * @param {Object} data - 약국 선택 데이터
   * @returns {Promise<BaseResponse<any>>}
   */
  async selectPharmacy(prescriptionId, data) {
    try {
      LogUtil.log('📤 [PrescriptionService] 약국 선택 요청:', {
        prescriptionId,
        data,
      });

      // 데이터 검증
      if (!prescriptionId) {
        throw new Error('처방전 ID가 필요합니다.');
      }
      if (!data.pharmacy_id) {
        throw new Error('약국 ID가 필요합니다.');
      }

      const result = await this.prescriptionRepository.selectPharmacy(
        prescriptionId,
        data
      );
      LogUtil.log('📥 [PrescriptionService] 약국 선택 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] 약국 선택 실패:', error);
      throw error;
    }
  }

  /**
   * 처방전 삭제
   * @param {number} prescriptionId - 처방전 ID
   * @returns {Promise<BaseResponse<any>>}
   */
  async deletePrescription(prescriptionId) {
    try {
      LogUtil.log('📤 [PrescriptionService] 처방전 삭제 요청:', prescriptionId);

      if (!prescriptionId) {
        throw new Error('처방전 ID가 필요합니다.');
      }

      const result =
        await this.prescriptionRepository.deletePrescription(prescriptionId);
      LogUtil.log('📥 [PrescriptionService] 처방전 삭제 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] 처방전 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 처방전 이미지 분석 (OCR)
   * @param {Object} data - 분석 요청 데이터
   * @param {string} data.prescription_image_url - 처방전 이미지 URL
   * @returns {Promise<BaseResponse<any>>}
   */
  async analyzePrescription(data) {
    try {
      LogUtil.log('📤 [PrescriptionService] 처방전 분석 요청:', data);

      // 데이터 검증
      if (!data.prescription_image_url) {
        throw new Error('처방전 이미지 URL이 필요합니다.');
      }

      const result =
        await this.prescriptionRepository.analyzePrescription(data);
      LogUtil.log('📥 [PrescriptionService] 처방전 분석 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] 처방전 분석 실패:', error);
      throw error;
    }
  }

  // ==================== 대시보드용 API (약국 인증 필요) ====================

  /**
   * [대시보드] 복약 요청 목록 조회
   * @param {Object} params - 쿼리 파라미터
   * @param {string} [params.status] - 상태 필터 (쉼표 구분 가능: requested,approved)
   * @param {number} [params.page=1] - 페이지 번호
   * @param {number} [params.size=20] - 페이지 크기 (최대 100)
   * @param {string} [params.start_date] - 시작일 (YYYY-MM-DD)
   * @param {string} [params.end_date] - 종료일 (YYYY-MM-DD)
   * @param {Object} [requestConfig] - axios 요청 설정 (예: { _skipAuthRetry: true })
   * @returns {Promise<BaseResponse<any>>}
   */
  async getOrderList(params = {}, requestConfig = {}) {
    try {
      LogUtil.log('📤 [PrescriptionService] 복약 요청 목록 조회 요청:', params);

      // 약국 토큰 가져오기
      const pharmacyAuthService = getPharmacyAuthService();
      const token = pharmacyAuthService.getPharmacyAccessToken();

      if (!token) {
        throw new Error('약국 인증이 필요합니다. 로그인해주세요.');
      }

      const result = await this.prescriptionRepository.getOrderList(params, token, requestConfig);
      LogUtil.log('📥 [PrescriptionService] 복약 요청 목록 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] 복약 요청 목록 조회 실패:', error);
      throw error;
    }
  }

  /**
   * [대시보드] 복약 요청 상태 변경
   * @param {number} orderId - 주문 ID
   * @param {Object} data - 상태 변경 데이터
   * @param {string} data.status - 변경할 상태 (waiting, approved, rejected, preparing, completed, picked_up)
   * @param {number} [data.total_amount] - 결제 금액 (approved 시 필수)
   * @param {string} [data.rejection_reason] - 거절 사유 (rejected 시 필수)
   * @param {string} [data.preparation_message] - 조제 메시지
   * @returns {Promise<BaseResponse<any>>}
   */
  async updateOrderStatus(orderId, data) {
    try {
      LogUtil.log('📤 [PrescriptionService] 복약 요청 상태 변경 요청:', {
        orderId,
        data,
      });

      // 데이터 검증
      if (!orderId) {
        throw new Error('주문 ID가 필요합니다.');
      }
      if (!data.status) {
        throw new Error('변경할 상태가 필요합니다.');
      }

      // 상태별 필수값 검증
      const validStatuses = ['waiting', 'approved', 'rejected', 'preparing', 'completed', 'picked_up'];
      if (!validStatuses.includes(data.status)) {
        throw new Error(`유효하지 않은 상태입니다. 허용된 상태: ${validStatuses.join(', ')}`);
      }

      if (data.status === 'approved' && !data.total_amount) {
        throw new Error('승인 시 결제 금액(total_amount)이 필요합니다.');
      }

      if (data.status === 'rejected' && !data.rejection_reason) {
        throw new Error('거절 시 거절 사유(rejection_reason)가 필요합니다.');
      }

      // 약국 토큰 가져오기
      const pharmacyAuthService = getPharmacyAuthService();
      const token = pharmacyAuthService.getPharmacyAccessToken();

      if (!token) {
        throw new Error('약국 인증이 필요합니다. 로그인해주세요.');
      }

      const result = await this.prescriptionRepository.updateOrderStatus(
        orderId,
        data,
        token
      );
      LogUtil.log('📥 [PrescriptionService] 복약 요청 상태 변경 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] 복약 요청 상태 변경 실패:', error);
      throw error;
    }
  }

  /**
   * [대시보드] 복약 요청 상세 조회
   * @param {number} orderId - 주문 ID
   * @returns {Promise<BaseResponse<any>>}
   */
  async getOrderDetail(orderId) {
    try {
      LogUtil.log('📤 [PrescriptionService] 복약 요청 상세 조회 요청:', orderId);

      if (!orderId) {
        throw new Error('주문 ID가 필요합니다.');
      }

      // 약국 토큰 가져오기
      const pharmacyAuthService = getPharmacyAuthService();
      const token = pharmacyAuthService.getPharmacyAccessToken();

      if (!token) {
        throw new Error('약국 인증이 필요합니다. 로그인해주세요.');
      }

      const result = await this.prescriptionRepository.getOrderDetail(orderId, token);
      LogUtil.log('📥 [PrescriptionService] 복약 요청 상세 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] 복약 요청 상세 조회 실패:', error);
      throw error;
    }
  }

  /**
   * [대시보드] 환자 목록 조회
   * @param {Object} params - 쿼리 파라미터
   * @returns {Promise<BaseResponse<any>>}
   */
  async getPatients(params = {}) {
    try {
      LogUtil.log('📤 [PrescriptionService] 환자 목록 조회 요청:', params);

      const pharmacyAuthService = getPharmacyAuthService();
      const token = pharmacyAuthService.getPharmacyAccessToken();

      if (!token) {
        throw new Error('약국 인증이 필요합니다. 로그인해주세요.');
      }

      const result = await this.prescriptionRepository.getPatients(params, token);
      LogUtil.log('📥 [PrescriptionService] 환자 목록 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] 환자 목록 조회 실패:', error);
      throw error;
    }
  }

  /**
   * [대시보드] 환자 상세 조회
   * @param {number} userId - 사용자 ID
   * @returns {Promise<BaseResponse<any>>}
   */
  async getPatientDetail(userId) {
    try {
      LogUtil.log('📤 [PrescriptionService] 환자 상세 조회 요청:', userId);

      if (!userId) {
        throw new Error('사용자 ID가 필요합니다.');
      }

      const pharmacyAuthService = getPharmacyAuthService();
      const token = pharmacyAuthService.getPharmacyAccessToken();

      if (!token) {
        throw new Error('약국 인증이 필요합니다. 로그인해주세요.');
      }

      const result = await this.prescriptionRepository.getPatientDetail(userId, token);
      LogUtil.log('📥 [PrescriptionService] 환자 상세 조회 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] 환자 상세 조회 실패:', error);
      throw error;
    }
  }

  /**
   * [대시보드] 처방전 QR 코드 생성
   * @param {Object} data - QR 생성 요청 데이터
   * @param {string} data.prescription_image_url - 처방전 이미지 URL
   * @param {string} [data.qr_type='paper'] - QR 타입
   * @returns {Promise<BaseResponse<any>>}
   */
  async generateQr(data) {
    try {
      LogUtil.log('📤 [PrescriptionService] QR 코드 생성 요청:', data);

      if (!data.prescription_image_url) {
        throw new Error('처방전 이미지 URL이 필요합니다.');
      }

      const pharmacyAuthService = getPharmacyAuthService();
      const token = pharmacyAuthService.getPharmacyAccessToken();

      if (!token) {
        throw new Error('약국 인증이 필요합니다. 로그인해주세요.');
      }

      const result = await this.prescriptionRepository.generateQr(data, token);
      LogUtil.log('📥 [PrescriptionService] QR 코드 생성 성공:', result);
      return result;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionService] QR 코드 생성 실패:', error);
      throw error;
    }
  }

  /**
   * [대시보드] AI 인사이트 조회 (현재 mock — 백엔드 준비 시 swap)
   */
  async getAiInsight(orderId) {
    if (!orderId) throw new Error('주문 ID가 필요합니다.');
    return await this.prescriptionRepository.getAiInsight(orderId);
  }

  /**
   * [대시보드] 대체조제 후보 조회 (현재 mock)
   */
  async getSubstitutions(orderId) {
    if (!orderId) throw new Error('주문 ID가 필요합니다.');
    return await this.prescriptionRepository.getSubstitutions(orderId);
  }

  /**
   * [환자 상세] 마지막 처방전 조회
   * GET /prescriptions/last-prescription/{userId}
   */
  async getLastPrescription(userId) {
    if (!userId) throw new Error('사용자 ID가 필요합니다.');
    return await this.prescriptionRepository.getLastPrescription(userId);
  }

  /**
   * [환자 상세] 환자별 처방전 리스트 (N개)
   * GET /prescriptions/?user_id={userId}
   */
  async getUserPrescriptions(userId, params = {}) {
    if (!userId) throw new Error('사용자 ID가 필요합니다.');
    return await this.prescriptionRepository.getUserPrescriptions(userId, params);
  }

  /**
   * [환자 상세] 복약지도문 (다건, edi_code 기반)
   * POST /medications/detail/list
   */
  async getMedicationGuide(ediCodes) {
    if (!Array.isArray(ediCodes) || ediCodes.length === 0) {
      throw new Error('edi_codes 배열이 비어있습니다.');
    }
    return await this.prescriptionRepository.getMedicationGuide(ediCodes);
  }
}
